import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LiquorOutlinedIcon from "@mui/icons-material/LiquorOutlined";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import CustomDataGrid from "../DataGrid/DataGrid";
import InventoryEmptyState from "./InventoryEmptyState";
import LocationSelect from "./pickers/LocationSelect";
import SkuSelect from "./pickers/SkuSelect";
import { unitOptionTokens, useUnitName } from "./pickers/useUnitName";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchOpenContainers,
  fetchExpiringContainers,
  openContainer,
  finishContainer,
} from "../../store/openContainerStore/openContainerThunk";
import { ApiStatus, defaultRowsPerPageOptions } from "../../utils/api.types";

const OpenContainersList: FC = () => {
  const dispatch = useAppDispatch();
  const { containers, status } = useAppSelector((state) => state.openContainers);

  const [locationId, setLocationId] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // open-a-bottle dialog state
  const [dSku, setDSku] = useState<any>(null);
  const [dSkuId, setDSkuId] = useState("");
  const [dLocationId, setDLocationId] = useState("");
  const [dContainerUnit, setDContainerUnit] = useState("");
  const unitName = useUnitName();

  // When an item is picked, default the container unit to its first pack (e.g.
  // "bottle"); a pack is what a container maps to. Falls back to the base unit.
  const onChangeSku = (sku: any) => {
    setDSku(sku);
    setDSkuId(sku?._id || "");
    const tokens = unitOptionTokens(sku);
    const packs = sku?.unitConversions ? Object.keys(sku.unitConversions) : [];
    setDContainerUnit(packs[0] || tokens[0] || "");
  };

  const reload = () => {
    const params = locationId ? `locationId=${locationId}` : "";
    if (expiringOnly) dispatch(fetchExpiringContainers(params));
    else dispatch(fetchOpenContainers(`${params}${params ? "&" : ""}status=open`));
  };

  useEffect(() => {
    reload();
  }, [dispatch, locationId, expiringOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const onOpen = async () => {
    if (!dSkuId || !dLocationId) return;
    try {
      await dispatch(openContainer({ skuId: dSkuId, locationId: dLocationId, containerUnit: dContainerUnit || "bottle" })).unwrap();
      setDialogOpen(false);
      setDSkuId("");
      setDSku(null);
      setDContainerUnit("");
      reload();
    } catch {
      /* toast handled in slice */
    }
  };

  const onFinish = async (id: string) => {
    try {
      await dispatch(finishContainer(id)).unwrap();
      reload();
    } catch {
      /* toast handled in slice */
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "sku", headerName: "Item", flex: 1.3, minWidth: 150, valueGetter: (p: GridValueGetterParams) => p.row.skuId?.name || "" },
      { field: "location", headerName: "Location", flex: 1, minWidth: 120, valueGetter: (p: GridValueGetterParams) => p.row.locationId?.name || "" },
      {
        field: "remaining",
        headerName: "Remaining / Opened",
        flex: 1,
        minWidth: 150,
        valueGetter: (p: GridValueGetterParams) => `${p.row.remainingVolume ?? 0} / ${p.row.openedVolume ?? 0} ${p.row.skuId?.baseUnitName || ""}`,
      },
      {
        field: "expiresAt",
        headerName: "Expires",
        flex: 1,
        minWidth: 130,
        renderCell: ({ row }: GridValueGetterParams) => {
          if (!row.expiresAt) return <span>—</span>;
          const exp = new Date(row.expiresAt);
          const overdue = exp.getTime() <= Date.now();
          return <span style={{ color: overdue ? "#c33b3b" : "inherit" }}>{exp.toLocaleDateString()}</span>;
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.7,
        minWidth: 90,
        renderCell: ({ row }: GridValueGetterParams) => <Chip size="small" label={row.status} />,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.7,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row }: GridValueGetterParams) =>
          row.status === "open" ? (
            <Button size="small" onClick={() => onFinish(row._id)}>
              Finish
            </Button>
          ) : null,
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={2} sx={{ py: 1, alignItems: "center" }}>
        <Grid item xs={12} sm={4}>
          <LocationSelect value={locationId} onChange={setLocationId} label="Filter by location" activeOnly={false} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <FormControlLabel
            control={<Switch checked={expiringOnly} onChange={(e) => setExpiringOnly(e.target.checked)} />}
            label="Expiring only"
          />
        </Grid>
        <Grid item xs={6} sm={4} sx={{ textAlign: "right" }}>
          <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => setDialogOpen(true)}>
            Open a bottle
          </Button>
        </Grid>
      </Grid>

      {status !== ApiStatus.LOADING && (containers?.length || 0) === 0 ? (
        <InventoryEmptyState
          height="75vh"
          icon={<LiquorOutlinedIcon />}
          title={expiringOnly ? "No Expiring Containers" : "No Open Containers"}
          description={
            expiringOnly
              ? "No open containers are nearing expiry for the selected filters. Adjust the filters or open a new container."
              : "Track partially-used bottles and packs by opening your first container. Monitor remaining volume and expiry at a glance."
          }
          actionLabel="Open a bottle"
          onAction={() => setDialogOpen(true)}
          caption="Need help? Check the Guide tab on inventory management"
        />
      ) : (
        <div style={{ height: "75vh", width: "100%" }}>
          <CustomDataGrid
            columns={columns}
            rows={containers}
            rowCount={containers.length}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            getRowId={(row: any) => row._id || Math.random().toString()}
            loading={status === ApiStatus.LOADING}
          />
        </div>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Open a container</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <SkuSelect value={dSkuId} onChange={onChangeSku} />
            </Grid>
            <Grid item xs={12} sm={7}>
              <LocationSelect value={dLocationId} onChange={setDLocationId} activeOnly={false} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                select
                fullWidth
                size="small"
                label="Container unit"
                value={dContainerUnit}
                onChange={(e) => setDContainerUnit(e.target.value)}
                disabled={!dSku}
                helperText={dSku ? "the pack a container maps to (e.g. bottle)" : "select an item first"}
              >
                {unitOptionTokens(dSku).map((t: string) => (
                  <MenuItem key={t} value={t}>{unitName(t)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onOpen} disabled={!dSkuId || !dLocationId}>
            Open
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpenContainersList;
